/**
 * Workflow Validation and Cleanup Utilities
 * 
 * This module provides validation and cleanup functions to ensure workflows
 * are valid before sending to the backend API. It addresses issues like:
 * - Edges referencing deleted nodes
 * - Missing required nodes (submission forms, end nodes)
 * - Invalid node configurations
 */

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  fixedIssues: string[];
  cleanedEdges: string[];
}

export interface CleanupResult {
  cleanedWorkflow: any;
  validationResult: ValidationResult;
}

export class WorkflowValidator {
  /**
   * Quick validation without cleanup - just checks if workflow is valid
   */
  static quickValidate(workflow: any): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      fixedIssues: [],
      cleanedEdges: []
    };

    if (!workflow) {
      result.isValid = false;
      result.errors.push('Workflow data is required');
      return result;
    }

    const { nodes = [], edges = [] } = workflow;

    // Check for orphaned edges
    const nodeIds = new Set(nodes.map((node: any) => node.id));
    const orphanedEdges = edges.filter((edge: any) => 
      !nodeIds.has(edge.source) || !nodeIds.has(edge.target)
    );

    if (orphanedEdges.length > 0) {
      result.isValid = false;
      result.errors.push(`Found ${orphanedEdges.length} edges referencing deleted nodes`);
    }

    // Check for submission form
    const hasSubmissionForm = nodes.some((node: any) => 
      node.type === 'submission-form' || node.data?.isSubmissionForm
    );

    if (!hasSubmissionForm) {
      result.isValid = false;
      result.errors.push('Workflow must have a submission form');
    }

    // Check for processing nodes
    const hasProcessingNodes = nodes.some((node: any) => 
      !(node.type === 'submission-form' || node.data?.isSubmissionForm)
    );

    if (!hasProcessingNodes) {
      result.isValid = false;
      result.errors.push('Workflow must have at least one processing node');
    }

    return result;
  }

  /**
   * Comprehensive validation with detailed checks
   * @param workflow - Workflow to validate
   * @param isDraft - Whether this is a draft workflow (more lenient validation)
   */
  static validateWorkflow(workflow: any, isDraft: boolean = true): ValidationResult {
    const result: ValidationResult = {
      isValid: true,
      errors: [],
      warnings: [],
      fixedIssues: [],
      cleanedEdges: []
    };

    if (!workflow) {
      result.isValid = false;
      result.errors.push('Workflow data is required');
      return result;
    }

    const { nodes = [], edges = [], metadata } = workflow;

    // Validate metadata
    if (metadata) {
      if (!metadata.title || metadata.title.trim() === '') {
        result.isValid = false;
        result.errors.push('Workflow name is required');
      }

      if (metadata.description && metadata.description.trim() === '') {
        result.warnings.push('Workflow description is empty');
      }
    }

    // Validate nodes
    if (nodes.length === 0) {
      result.isValid = false;
      result.errors.push('Workflow must have at least one node');
      return result;
    }

    // Check for duplicate node IDs
    const nodeIds = nodes.map((node: any) => node.id);
    const duplicateIds = nodeIds.filter((id: string, index: number) => nodeIds.indexOf(id) !== index);
    if (duplicateIds.length > 0) {
      result.isValid = false;
      result.errors.push(`Duplicate node IDs found: ${duplicateIds.join(', ')}`);
    }

    // Check for submission form
    const submissionFormNodes = nodes.filter((node: any) => 
      node.type === 'submission-form' || node.data?.isSubmissionForm
    );

    if (submissionFormNodes.length === 0) {
      result.isValid = false;
      result.errors.push('Workflow must have a submission form');
    } else if (submissionFormNodes.length > 1) {
      result.warnings.push('Multiple submission forms found - only one should be used');
    }

    // Check for processing nodes
    const processingNodes = nodes.filter((node: any) => 
      !(node.type === 'submission-form' || node.data?.isSubmissionForm)
    );

    if (processingNodes.length === 0) {
      result.isValid = false;
      result.errors.push('Workflow must have at least one processing node');
    }

    // Check for deprecated start nodes
    const startNodes = nodes.filter((node: any) => node.type === 'start');
    if (startNodes.length > 0) {
      result.warnings.push('Start nodes are deprecated - use submission forms instead');
    }

    // Validate edges
    const nodeIdSet = new Set(nodeIds);
    const orphanedEdges = edges.filter((edge: any) => 
      !nodeIdSet.has(edge.source) || !nodeIdSet.has(edge.target)
    );

    if (orphanedEdges.length > 0) {
      result.isValid = false;
      result.errors.push(`Found ${orphanedEdges.length} edges referencing non-existent nodes`);
      orphanedEdges.forEach((edge: any) => {
        if (!nodeIdSet.has(edge.source)) {
          result.errors.push(`Edge references non-existent source node: ${edge.source}`);
        }
        if (!nodeIdSet.has(edge.target)) {
          result.errors.push(`Edge references non-existent target node: ${edge.target}`);
        }
      });
    }

    // Check for isolated nodes (nodes with no connections)
    const connectedNodes = new Set();
    edges.forEach((edge: any) => {
      connectedNodes.add(edge.source);
      connectedNodes.add(edge.target);
    });

    const isolatedNodes = nodes.filter((node: any) => 
      !connectedNodes.has(node.id) && 
      !(node.type === 'submission-form' || node.data?.isSubmissionForm)
    );

    if (isolatedNodes.length > 0) {
      result.warnings.push(`Found ${isolatedNodes.length} isolated nodes with no connections`);
    }

    return result;
  }

  /**
   * Clean up workflow by removing orphaned edges and fixing common issues
   */
  static cleanupWorkflow(workflow: any): any {
    if (!workflow) {
      return workflow;
    }

    const { nodes = [], edges = [], ...rest } = workflow;

    // Get valid node IDs
    const nodeIds = new Set(nodes.map((node: any) => node.id));

    // Filter out orphaned edges
    const validEdges = edges.filter((edge: any) => 
      nodeIds.has(edge.source) && nodeIds.has(edge.target)
    );

    // Remove duplicate edges
    const edgeSet = new Set();
    const deduplicatedEdges = validEdges.filter((edge: any) => {
      const edgeKey = `${edge.source}->${edge.target}`;
      if (edgeSet.has(edgeKey)) {
        return false;
      }
      edgeSet.add(edgeKey);
      return true;
    });

    return {
      ...rest,
      nodes,
      edges: deduplicatedEdges
    };
  }

  /**
   * Main function to clean up and validate workflow before saving
   */
  static cleanupWorkflowForSave(workflow: any): CleanupResult {
    // First, clean up the workflow
    const cleanedWorkflow = this.cleanupWorkflow(workflow);

    // Track what was cleaned up
    const originalEdgeCount = workflow.edges?.length || 0;
    const cleanedEdgeCount = cleanedWorkflow.edges?.length || 0;
    const removedEdgeCount = originalEdgeCount - cleanedEdgeCount;

    // Validate the cleaned workflow (use draft validation for auto-save)
    const validationResult = this.validateWorkflow(cleanedWorkflow, true); // isDraft = true for auto-save

    // Add cleanup information to the result
    if (removedEdgeCount > 0) {
      validationResult.fixedIssues.push(`Removed ${removedEdgeCount} orphaned edge(s)`);
      
      // Find which edges were removed
      const originalEdges = workflow.edges || [];
      const cleanedEdges = cleanedWorkflow.edges || [];
      const cleanedEdgeKeys = new Set(cleanedEdges.map((e: any) => `${e.source}->${e.target}`));
      
      originalEdges.forEach((edge: any) => {
        const edgeKey = `${edge.source}->${edge.target}`;
        if (!cleanedEdgeKeys.has(edgeKey)) {
          validationResult.cleanedEdges.push(edgeKey);
        }
      });
    }

    return {
      cleanedWorkflow,
      validationResult
    };
  }

  /**
   * Validate that a workflow can be published
   */
  static validateForPublishing(workflow: any): ValidationResult {
    const result = this.validateWorkflow(workflow, false); // isDraft = false for publishing

    // Additional checks for publishing
    const { nodes = [] } = workflow;

    // Check that all nodes have proper configuration
    const unconfiguredNodes = nodes.filter((node: any) => {
      if (node.type === 'submission-form' || node.data?.isSubmissionForm) {
        const config = node.data?.config || node.config;
        return !config?.fields || config.fields.length === 0;
      }
      
      // Check if processing nodes have basic configuration
      const config = node.data?.config || node.config;
      if (!config || Object.keys(config).length === 0) {
        return true;
      }

      return false;
    });

    if (unconfiguredNodes.length > 0) {
      result.warnings.push(`${unconfiguredNodes.length} nodes may need additional configuration before publishing`);
    }

    return result;
  }
}

/**
 * Convenience function for the most common use case
 */
export function cleanupWorkflowForSave(workflow: any): CleanupResult {
  return WorkflowValidator.cleanupWorkflowForSave(workflow);
}

/**
 * Quick validation function for form validation
 */
export function quickValidateWorkflow(workflow: any): ValidationResult {
  return WorkflowValidator.quickValidate(workflow);
}

/**
 * Clean up edges when a node is deleted (for use in the editor)
 */
export function cleanupEdgesForDeletedNode(edges: any[], deletedNodeId: string): any[] {
  return edges.filter(edge => 
    edge.source !== deletedNodeId && edge.target !== deletedNodeId
  );
}

/**
 * Validate edge before adding (for use in the editor)
 */
export function validateNewEdge(edge: any, existingNodes: any[]): { isValid: boolean; error?: string } {
  const nodeIds = new Set(existingNodes.map(node => node.id));
  
  if (!nodeIds.has(edge.source)) {
    return { isValid: false, error: `Source node ${edge.source} does not exist` };
  }
  
  if (!nodeIds.has(edge.target)) {
    return { isValid: false, error: `Target node ${edge.target} does not exist` };
  }
  
  return { isValid: true };
} 